import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { GlobalStateService } from 'src/app/services/global/global-state.service';
import { Invoice } from 'src/app/shared/models/invoice.interface';

@Component({
  selector: 'app-commercial-invoice',
  templateUrl: './commercial-invoice.component.html',
  styleUrls: ['./commercial-invoice.component.scss']
})
export class CommercialInvoiceComponent implements OnInit, OnChanges {
  @Input() invoices: Invoice[] = [];
  @Input() viewMode: string = 'individual';
  @Output() editableDataChange = new EventEmitter<any>();

  settings: any = null;
  editableInvoices: any[] = [];

  constructor(private _globalService: GlobalStateService) { }

  ngOnInit(): void {
    this.loadSettings();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['invoices']) {
      this.invoices.forEach(invoice => {
        if (!invoice.vehicles) {
          invoice.vehicles = [invoice.vehicle];
        }
      });
      this.initializeEditableData();
    }
  }

  loadSettings(): void {
    this._globalService.GetSetting().subscribe((res: any) => {
      this.settings = res.contacts?.english;
    });
  }

  initializeEditableData(): void {
    this.editableInvoices = this.invoices.map(invoice => ({
      ...invoice,
      editableVehicles: (invoice.vehicles || []).map((v: any) => ({
        ...v,
        vehicle_id: v._id,
        editableInvoiceAmount: v.invoice_amount || (invoice.vehicles!.length === 1 ? invoice.invoice_amount : 0) || 0,
        editableOceanFreight: v.ocean_freight || 0,
        editableInsurance: v.car_insurance_refund || 0
      }))
    }));
    this.emitEditableData();
  }

  onFieldChange(): void {
    this.emitEditableData();
  }

  emitEditableData(): void {
    const extraData: any[] = [];
    this.editableInvoices.forEach((inv: any) => {
      inv.editableVehicles.forEach((v: any) => {
        extraData.push({
          vehicle_id: v.vehicle_id,
          invoice_amount: v.editableInvoiceAmount,
          ocean_freight: v.editableOceanFreight,
          insurance: v.editableInsurance
        });
      });
    });
    this.editableDataChange.emit(extraData);
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).toUpperCase();
  }

  formatCurrency(amount: number | null, currency: string | null = 'USD'): string {
    if (!amount || amount === 0) return '-';
    const curr = currency || 'USD';
    return `${curr} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  getChassisNumber(invoice: Invoice): string {
    return invoice.vehicle?.chassis_no || '-';
  }

  getStockNumber(invoice: Invoice): string {
    return invoice.vehicle?.car_no || '-';
  }

  getVehicleDescription(invoice: Invoice): string {
    const make = invoice.vehicle?.make || '';
    const model = invoice.vehicle?.model || '';
    const year = invoice.vehicle?.year || '';
    return `${year} ${make} ${model}`.trim();
  }

  getCurrentDate(): string {
    return this.formatDate(new Date().toISOString());
  }

  getHSCode(invoice: Invoice): string {
    return invoice.vehicle?.hs_code || '8703';
  }

  getTotalAmount(): string {
    if (this.editableInvoices.length > 0) {
      const total = this.editableInvoices.reduce((sum: number, inv: any) =>
        sum + inv.editableVehicles.reduce((vSum: number, v: any) =>
          vSum + (Number(v.editableInvoiceAmount) || 0) + (Number(v.editableOceanFreight) || 0) + (Number(v.editableInsurance) || 0), 0), 0);
      return `$ ${total.toLocaleString('en-US')}`;
    }
    const total = this.invoices.reduce((sum, invoice) => sum + (invoice.invoice_amount || 0), 0);
    return `$ ${total.toLocaleString('en-US')}`;
  }

  getTotalQuantity(): number {
    return this.invoices[0]?.vehicles?.length || 0;
  }
}
